import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "@web/route-services/root/AppContext";

/**
 * A mutation that optimistically removes a character from a chat.
 * @param chatID - The ID of the chat to unlink the character from.
 * @returns A mutation function that can be called to unlink a character from the chat.
 */
export const useUnlinkCharacter = (chatID: number) => {
  const { access } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (characterID: number) => {
      await access.chat.unlinkCharacter(chatID, characterID);
    },
    onMutate: async (characterID) => {
      await queryClient.cancelQueries({ queryKey: ["chatCharacters", chatID] });
      const previousCharacters = queryClient.getQueryData<Map<number, any>>(["chatCharacters", chatID]);
      queryClient.setQueryData(["chatCharacters", chatID], (old: Map<number, any>) => {
        const newMap = new Map(old);
        newMap.delete(characterID);
        return newMap;
      });

      return { previousCharacters };
    },
    onError: (err, newCharacter, context) => {
      queryClient.setQueryData(["chatCharacters", chatID], context?.previousCharacters);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["chatCharacters", chatID] });
    }
  });
};
